package com.steamstreet.vegasful.browser.account.intelligence

import com.steamstreet.vegasful.graphql.api.client.ConciergePick
import com.steamstreet.vegasful.graphql.api.client.Entity
import com.steamstreet.vegasful.graphql.api.client.Event
import com.steamstreet.vegasful.graphql.api.client.ThingToDo
import com.steamstreet.vegasful.graphql.api.client._ConciergePickQuery
import com.steamstreet.vegasful.graphql.api.client._EntityQuery
import com.steamstreet.vegasful.web.concierge.dateSpan
import com.steamstreet.vegasful.web.concierge.stubLabel
import kotlinx.datetime.LocalDate
import kotlinx.html.*

/**
 * Everything the answer's cards read from a pick. Selecting less compiles but fails when the card
 * is drawn, because the generated client throws for a field the query did not select.
 */
internal fun _ConciergePickQuery.pickFields() {
    reason
    dates
    time
    entity { cardFields() }
}

/** Everything a card, or a row of "More that came up", reads from an entity. */
internal fun _EntityQuery.cardFields() {
    id
    name
    path
    __typename
    images {
        uri
        large { uri }
        withWidth(768) { uri }
    }
    onEvent {
        date
        time
        venue {
            id
            name
            path
            __typename
        }
        tags {
            id
            name
            path
        }
    }
    // `venue` has to be selected identically here and under `onEvent`: GraphQL requires a field
    // selected under the same response key in two fragments to have the same shape.
    onThingToDo {
        venue {
            id
            name
            path
            __typename
        }
    }
}

/**
 * A finished turn: a note if the concierge has one, the picks dealt as cards with the top pick
 * wide and the rest in a grid, then what else came up. The page's stylesheet (`CONCIERGE_CSS` in
 * the server's `Concierge.kt`) styles every class used here.
 */
internal fun TagConsumer<*>.conciergeAnswer(
    picks: List<ConciergePick>,
    more: List<Entity>,
    message: String?,
    today: LocalDate
) {
    div("cg-answer") {
        message?.let { text ->
            div("cg-note") { p { +text } }
        }

        if (picks.isNotEmpty()) {
            div("cg-answer-head") {
                h2 { +if (picks.size == 1) "My pick for you" else "${picks.size} picks for you" }
                val showings = picks.sumOf { maxOf(1, it.dates.size) }
                p {
                    +listOfNotNull(
                        "$showings showings".takeIf { showings > picks.size },
                        dateSpan(picks.flatMap { it.dates }),
                        "best first".takeIf { picks.size > 1 }
                    ).joinToString(" · ")
                }
            }

            val lead = picks.first().takeIf { it.entity.images?.firstOrNull() != null }
            if (lead != null) pickCard(lead, 1, lead = true, today = today)
            val rest = if (lead != null) picks.drop(1) else picks
            if (rest.isNotEmpty()) {
                div("cg-grid") {
                    rest.forEachIndexed { index, pick ->
                        pickCard(pick, index + 1 + (if (lead != null) 1 else 0), lead = false, today = today)
                    }
                }
            }
        }

        if (more.isNotEmpty()) {
            div("cg-more-results") {
                h3 { +if (picks.isEmpty()) "Here's what I found" else "More that came up" }
                div("cg-more-list") {
                    more.forEach { moreItem(it) }
                }
            }
        }
    }
}

/**
 * The card for one pick. Every card has the same parts in the same places, whether or not it is
 * the lead: the photo with the ticket stub and rank, then the name, venue, tags, the concierge's
 * reason, and the actions.
 */
private fun FlowContent.pickCard(pick: ConciergePick, rank: Int, lead: Boolean, today: LocalDate) {
    val entity = pick.entity
    val name = entity.name ?: return
    val href = entity.path
    val image = entity.images?.firstOrNull()
    val event = entity as? Event
    val venue = event?.venue?.name ?: (entity as? ThingToDo)?.venue?.name
    val tags = event?.tags.orEmpty().map { it.name }.sortedByDescending { it.equals("free", true) }.take(3)
    val stub = stubLabel(pick.dates, pick.time, today)

    article(if (lead) "cg-pick cg-lead" else "cg-pick") {
        attributes["style"] = "--i:${rank - 1}"

        div("cg-pick-media") {
            val src = if (lead) image?.large?.uri ?: image?.uri else image?.withWidth?.uri ?: image?.large?.uri ?: image?.uri
            if (src != null) {
                a(href = href, classes = "cg-pick-photo") {
                    attributes["tabindex"] = "-1"
                    attributes["aria-hidden"] = "true"
                    img(src = src, alt = "") { attributes["loading"] = "lazy" }
                }
            }
            if (stub != null) {
                div("cg-stub") {
                    attributes["aria-label"] = listOfNotNull(stub.days, stub.dates, stub.time).joinToString(", ")
                    span("d") { +stub.days }
                    span("m") { +stub.dates }
                    stub.time?.let { span("t") { +it } }
                }
            }
            span("cg-rank") {
                attributes["title"] = "Pick $rank"
                +"$rank"
            }
        }

        div("cg-pick-body") {
            if (lead) span("cg-lead-flag") { +"✦ Top pick" }
            h3("cg-pick-name") {
                if (href != null) a(href = href) { +name } else +name
            }
            venue?.let {
                div("cg-venue") {
                    unsafe { +PIN_ICON }
                    span { +it }
                }
            }
            if (tags.isNotEmpty()) {
                div("cg-tags") {
                    tags.forEach { tag ->
                        span(if (tag.equals("free", ignoreCase = true)) "cg-tag free" else "cg-tag") { +tag }
                    }
                }
            }
            pick.reason?.takeIf { it.isNotBlank() }?.let { reason ->
                p("cg-why") {
                    b { +"Why it fits" }
                    span { +reason }
                }
                if (!lead) {
                    button(classes = "cg-more") {
                        type = ButtonType.button
                        attributes["hidden"] = "hidden"
                        +"Read more"
                    }
                }
            }
            if (href != null) {
                div("cg-acts") {
                    a(href = href, classes = "cg-details") { +"See details →" }
                    // The entity page's own like control, started once the answer is on the page.
                    div("cg-save") {
                        attributes["data-entity-interactions"] = "1"
                        attributes["data-entity-path"] = href
                        attributes["data-entity-name"] = name
                        attributes["data-interactions"] = "like"
                    }
                }
            }
        }
    }
}

/** A compact row for something the concierge found but did not choose. */
private fun FlowContent.moreItem(entity: Entity) {
    val name = entity.name ?: return
    val href = entity.path ?: return
    val event = entity as? Event
    val image = entity.images?.firstOrNull()
    a(href = href, classes = "cg-more-item") {
        val src = image?.withWidth?.uri ?: image?.uri
        if (src != null) img(src = src, alt = "") { attributes["loading"] = "lazy" } else span("cg-more-thumb") {}
        span {
            b { +name }
            val line = listOfNotNull(
                event?.venue?.name ?: (entity as? ThingToDo)?.venue?.name,
                event?.date?.let { stubLabel(listOf(it), event.time)?.let { s -> "${s.days} ${s.dates}" } }
            ).joinToString(" · ")
            if (line.isNotEmpty()) small { +line }
        }
    }
}

private const val PIN_ICON =
    """<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 21s-7-6.2-7-11.5A7 7 0 0 1 19 9.5C19 14.8 12 21 12 21z"/><circle cx="12" cy="9.5" r="2.5"/></svg>"""
